import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router'

import { useNotifications } from 'app/application/application'
import { Form, combineClassnames } from 'app/common/presentation'
import { CheckmarkIcon, ErrorIcon } from 'app/common/presentation/icons'
import {
  useConnections,
  useConnectionsApi,
  useGoogleAnalyticseConnectionApi,
} from 'app/connections/application'
import {
  GoogleAnalyticsAccount,
  GoogleAnalyticsProperty,
  GoogleAnalyticsTrackingCredentials,
} from 'app/connections/types'

import { Button } from 'components'

import Accounts from '../Accounts'
import Authorization from '../Authorization'
import setupStyles from '../Setup.module.scss'
import styles from './GoogleAnalyticsSettings.module.scss'
import { StepContext } from './StepWithConnectionAndAccounts'

type VerificationState = 'loading' | 'success' | 'error'

const GoogleAnalyticsSettings = () => {
  const navigate = useNavigate()
  const { createNotification, te } = useNotifications()
  const { accounts, connection } = useOutletContext<StepContext<GoogleAnalyticsAccount>>()
  const { getProperties } = useGoogleAnalyticseConnectionApi()
  const { validateConnection } = useConnectionsApi(connection.name)
  const { saveConnection } = useConnections()
  const { t } = useTranslation('connections')

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [properties, setProperties] = useState<GoogleAnalyticsProperty[]>([])
  const [property, setProperty] = useState<GoogleAnalyticsProperty>()
  const [measurementId, setMeasurementId] = useState<string>('')
  const [apiSecret, setApiSecret] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const [verificationStatus, setVerificationStatus] = useState<VerificationState>()

  // not empty check is done in enclosing component StepWithConnectionAndAccounts
  const account = accounts[0]
  const isValidGenericConfig = property && measurementId && apiSecret

  useEffect(
    () =>
      void getProperties(account.externalId)
        .then((result) => {
          if (result.ok) {
            setProperties(result.val)
          } else {
            createNotification({
              message: result.val.message,
              title: te(`http.${result.val.code}`),
              type: 'error',
            })
          }
        })
        .finally(() => setIsLoading(false)),
    [account, createNotification, getProperties, te],
  )

  const handleVerify = () => {
    setVerificationStatus('loading')
    validateConnection<GoogleAnalyticsTrackingCredentials>({
      id: property!.id,
      measurementId,
      secret: apiSecret,
    })
      .then((validateResult) => {
        if (validateResult.ok && validateResult.val) {
          setVerificationStatus('success')
        } else {
          setVerificationStatus('error')
        }
      })
      .catch(() => setVerificationStatus('error'))
  }

  const handleSave = useCallback(() => {
    setIsSaving(true)

    const connectionConfig = {
      propertyId: property!.id,
      measurementId,
      apiSecret,
    }

    void saveConnection(connection, connectionConfig)
      .then((result) => {
        if (result.ok) {
          createNotification({
            message: t('update.success.message', {
              name: t(`available.${connection.name}.displayName`),
            }),
            title: t('update.success.title'),
            type: 'success',
          })

          void navigate(`/connections/${connection.name}/setup`, { replace: true })
        } else {
          createNotification({
            message: result.val.message,
            title: te(`http.${result.val.code}`),
            type: 'error',
          })
        }
      })
      .finally(() => setIsSaving(false))
  }, [
    apiSecret,
    createNotification,
    measurementId,
    navigate,
    property,
    saveConnection,
    connection,
    t,
    te,
  ])

  /*
G-E6M6PLQ0Q4
    */
  return (
    <div>
      <div className={setupStyles.form}>
        <Authorization authorizationState={'VALID'} connection={connection} />
        <Accounts accounts={accounts} connectionName={connection.name} />
        <Form.DropDownFormRow
          data={properties}
          description={t(`setup.${connection.name}.config.propertyId.description`)}
          loading={isLoading}
          onSelect={setProperty}
          renderValueContent={(value) => `${value!.name} (${value!.id})`}
          selected={property}
          title={t(`setup.${connection.name}.config.propertyId.title`)}
        />
        <Form.InputFormRow
          description={t(`setup.${connection.name}.config.measurementId.description`)}
          link={
            property
              ? `https://analytics.google.com/analytics/web/#/a${account.id}p${property.id}/admin/streams/table/`
              : undefined
          }
          onChange={property ? setMeasurementId : undefined}
          placeholder={t(`setup.${connection.name}.config.measurementId.title`)}
          title={t(`setup.${connection.name}.config.measurementId.title`)}
          value={measurementId}
        />
        <Form.InputFormRow
          description={t(`setup.${connection.name}.config.apiSecret.description`)}
          onChange={property ? setApiSecret : undefined}
          placeholder={t(`setup.${connection.name}.config.apiSecret.title`)}
          title={t(`setup.${connection.name}.config.apiSecret.title`)}
          value={apiSecret}
        />
        <div className={styles.signals}>
          <div className={styles.labelContainer}>
            <span className={styles.label}>{t('setup.googleAnalytics.config.signals.title')}</span>
            <span className={styles.description}>
              {t('setup.googleAnalytics.config.signals.description')}
            </span>
          </div>

          <div className={styles.verifyContainer}>
            <Button
              onClick={handleVerify}
              loading={verificationStatus === 'loading'}
              disabled={!isValidGenericConfig || verificationStatus === 'loading'}
              variant="secondary"
            >
              {t(
                `setup.googleAnalytics.config.signals.verification.${verificationStatus === 'loading' ? 'loading' : 'verify'}`,
              )}
            </Button>

            {verificationStatus && ['error', 'success'].includes(verificationStatus) && (
              <div className={combineClassnames(styles.indicator, styles[verificationStatus])}>
                {verificationStatus === 'error' ? <ErrorIcon /> : <CheckmarkIcon />}
                {t(`setup.googleAnalytics.config.signals.verification.${verificationStatus}`)}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={setupStyles.actions}>
        <Button onClick={() => void navigate(-1)} variant="secondary">
          {t('setup.actions.back')}
        </Button>
        <Button
          disabled={verificationStatus !== 'success'}
          loading={isSaving}
          onClick={(e) => {
            e.stopPropagation()

            handleSave()
          }}
        >
          {t('setup.actions.finish')}
        </Button>
      </div>
    </div>
  )
}

export default GoogleAnalyticsSettings
